import * as React from 'react';
import { Dispatch, compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link , withRouter} from 'react-router-dom';
import { Button, ButtonGroup } from 'reactstrap';
import { LProps, RouterProps, withLocalization } from '../utils/wrappers';
import { Story, CompetitionPledge, UserRead, Entity, Comment } from '../userApi';
import { makePledgePath, makeUserPath, makeEntityPath } from '../utils/routerHelper';
import CommentComponent from './Comment';
import CommentPrompt from './CommentPrompt';
import { State, Toast } from '../redux/state';
import * as selectors from '../redux/selectors';
import actions from '../redux/actions';
import ProfilePhoto from './ProfilePhoto';
import { displayFullName, CompetitionPledges, isVideoUrl } from '../utils/userApiHelper';
import StoryEditor from './StoryEditor';
import * as moment from 'moment';
import LinkButton from './LinkButton';
import { TranslationFunction } from 'i18next';
import OpenGraphCarousel from './OpenGraphCarousel';
import displayContent from './displayContent';
import StoryControls from './StoryControl';
import StoryVideo from './StoryVideo';

const StoryContent = ({ story }: { story: Story }) => (
  <div className="cc__story__content">
    {displayContent(story.contentMD)}
  </div>
);

const StoryTitle = ({ pledge, t }: { pledge: CompetitionPledge, t: TranslationFunction }) =>
 (<div><Link to={makePledgePath(pledge, t)}>{pledge.shortTitle}</Link></div>);
interface ManualProps {
  story: Story;
}

interface Props extends ManualProps {
  pledge: CompetitionPledge;
  creator?: UserRead;
  university?: Entity;
  team?: Entity;
  userCompletionsLTPledgeRepeats: boolean;
  isUserEligibleForPledge: boolean;
  liked: boolean;
  flagged: boolean;
  comments: Comment[];
  isVerifiedCompetitionUser: boolean;
  isCreator: boolean;
  toggleLike: (story: Story) => void;
  toggleFlag: (story: Story) => void;
  editStory: () => void;
  openConfirmStoryDelete: () => void;
  toast: (toastData: Partial<Toast>, duration?: number) => void;
  editing: boolean;
}

const isVideo = (img: string) => isVideoUrl(img);

const calculatorFootprintFromStory = (contentMD: string): string | null => {
  const match = contentMD.match(/([\d.]+)\s+metric tons CO₂e/i);
  return match ? match[1] : null;
};

class StoryComponent extends React.Component<LProps<Props> & RouterProps<{}>> {
  public render() {
    const {
    t, formatNumber, story, pledge, creator,
    toggleLike, toggleFlag, liked, flagged, comments,
    university, team, userCompletionsLTPledgeRepeats, history, isVerifiedCompetitionUser,
    isCreator, editStory, openConfirmStoryDelete, editing, isUserEligibleForPledge
      } = this.props;

    if (editing) return (<StoryEditor story={story} />);
    const calculatorFootprint = pledge && pledge.uuid === CompetitionPledges.TakeCalculator
      ? calculatorFootprintFromStory(story.contentMD || '')
      : null;
    const usesDefaultPledgePhoto = Boolean(story.photo && pledge && story.photo === pledge.photo);
    const storyTimestamp = story.createdAt ? moment(story.createdAt).fromNow() : '';
    return (
      <div className="cc__story cc__white_box">
        {pledge ? <StoryTitle pledge={pledge} t={t} /> : <div>&nbsp;</div>}
        <hr />
        <div>
          <div className="cc__story__creator cc__story__creator">
            <div className="cc__story__creator__identity">
              <div className="cc__story__creator__identity__photo">
                <ProfilePhoto
                  profilePhoto={creator && creator.profilePhoto}
                  alt={creator ? creator.firstName : t('User')}
                />
              </div>
              <div className="cc__story__creator__identity cc__story__creator__name">
                {creator &&
                  <Link to={makeUserPath(story.competitionID, creator, t)}>{displayFullName(creator)}</Link>
                }
                {storyTimestamp &&
                  <div className="cc__story__creator__timestamp cc__lighttext">
                    {storyTimestamp}
                  </div>
                }
              </div>
            </div>
            {(university || team) &&
              <div className="cc__story__creator__entities">
                {university &&
                  <Link to={makeEntityPath(story.competitionID, university, t)}>{university.name}</Link>
                }
                {team && (!university || team.id !== university.id) &&
                  <Link to={makeEntityPath(story.competitionID, team, t)}>{team.name}</Link>
                }
              </div>
            }
          </div>
        </div>
        {story.photo &&
          <div className="cc__story__photo">
            {isVideo(story.photo) ?
              <StoryVideo src={story.photo} title={t('story video')} />
            :
              <img
                src={story.photo}
                alt={(pledge && pledge.shortTitle) || t('story image')}
                title={(pledge && pledge.shortTitle) || t('story image')}
                style={{
                  display: 'inline-block',
                  width: 'auto',
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  objectPosition: 'center center'
                }}
              />
            }
            {usesDefaultPledgePhoto && !calculatorFootprint &&
              <div className="cc__lighttext cc__story__photo_caption">
                {t('Example image — not this participant\'s footprint chart.')}
              </div>
            }
          </div>
        }
        {story.contentMD && <StoryContent story={story} />}
        {(story.links && story.links.length > 0) &&
          <OpenGraphCarousel links={story.links} />
        }
        {story.competitionUserPointID !== 0 && pledge &&
          <div className="cc__story__pledge_stats">
            <div>
              <div>
                <span>{t('Points')}</span>&nbsp;
                <span className="cc__lighttext">{formatNumber(story.pointsEarned, 0)}</span>
              </div>
              {Boolean(pledge.savingsCO2Lbs) &&
                <div>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('Pounds CO<sub>2</sub>')
                    }}
                  />&nbsp;
                  <span className="cc__lighttext">{formatNumber(pledge.savingsCO2Lbs, 0)}</span>
                </div>
              }
              {calculatorFootprint &&
                <div>
                  <span>{t('Carbon footprint (mt CO₂e)')}</span>&nbsp;
                  <span className="cc__lighttext">{calculatorFootprint}</span>
                </div>
              }
            </div>
            {isVerifiedCompetitionUser &&
              <div>
                {!userCompletionsLTPledgeRepeats &&
                  <img style={{ width: '36px' }} src="/assets/story/checkmark@2x.png" alt={t('action taken')} />
                }
                {(isUserEligibleForPledge && userCompletionsLTPledgeRepeats) &&
                  <Button
                    color="primary"
                    onClick={() => history.push(makePledgePath(pledge, t))}
                  >{t('Take action')}</Button>
                }
              </div>
            }
          </div>
        }
        <hr />
        {isVerifiedCompetitionUser &&
          <div>
            <div>
              <LinkButton
                onClick={() => toggleLike(story)}
                title={liked ? t('Unlike this post') : t('Like this post')}
                aria-label={liked ? t('Unlike this post') : t('Like this post')}
              ><img src={`/assets/story/${liked ? 'filled-' : ''}heart.png`} alt={liked ? t('Unlike this post') : t('Like this post')} /></LinkButton>
              <LinkButton
                onClick={() => toggleFlag(story)}
                title={flagged ? t('Remove report') : t('Report this post for review')}
                aria-label={flagged ? t('Remove report') : t('Report this post for review')}
              ><img src={`/assets/story/${flagged ? 'filled-' : ''}flag.png`} alt={flagged ? t('Remove report') : t('Report this post for review')} /></LinkButton>
            </div>
            <StoryControls
              t={t}
              editStory={editStory}
              openConfirmStoryDelete={openConfirmStoryDelete}
              storyID={story.id}
              isCreator={isCreator}
            />
          </div>
        }
        <div className="cc__bold">{story.likes} {story.likes === 1 ? t('like') : t('likes')}</div>
        <div id="story-comments">
          {comments.map((comment) =>
            <CommentComponent
              key={comment.id}
              comment={comment}
            />)}
        </div>
        {isVerifiedCompetitionUser &&
          <div>
            <hr />
            <CommentPrompt
              comment={{ commentableID: story.id, commentableType: Comment.CommentableTypeEnum.Story }}
            />
          </div>
        }
      </div>);
  }
}

const mapStateToProps = (state: State, ownProps: ManualProps) => {
  const pledge = ownProps.story.competitionPledgeID !== 0 ? state.competitionPledges[ownProps.story.competitionPledgeID] : null;
  return {
    pledge,
    creator: selectors.storyCreator(state, ownProps),
    isCreator: state.user && state.user.id === ownProps.story.createdBy,
    university: selectors.acceptedUniversity(state, ownProps.story.createdBy),
    team: selectors.acceptedTeam(state, ownProps.story.createdBy),
    userCompletionsLTPledgeRepeats: selectors.userCompletionsLTPledgeRepeats(state, ownProps),
    isUserEligibleForPledge: Boolean(pledge) && selectors.isUserEligibleForPledge(state, pledge),
    liked: Boolean(state.storyLikes && state.storyLikes[ownProps.story.id]),
    flagged: Boolean(state.storyFlags && state.storyFlags[ownProps.story.id]),
    comments: state.storyComments[ownProps.story.id] || [],
    isVerifiedCompetitionUser: selectors.isVerifiedCompetitionUser(state),
    editing: state.editingStoryID === ownProps.story.id
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: ManualProps) => bindActionCreators({
  toggleLike: actions.toggleStoryLike,
  toggleFlag: actions.toggleStoryFlag,
  editStory: () => actions.editStory(ownProps.story.id),
  openConfirmStoryDelete: () => actions.openConfirmStoryDelete(ownProps.story),
  toast: actions.toast
}, dispatch);

export default compose(
  withLocalization('story'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(StoryComponent) as React.SFC<ManualProps>;
